import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles } from '@material-ui/core/styles'
import { Alert } from '@material-ui/lab'
import { Typography, Grid, Box, Tooltip, Paper, LinearProgress } from '@material-ui/core'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ErrorIcon from '@material-ui/icons/Error'
import { green, orange, red, blue } from '@material-ui/core/colors'

import { obtenerMovimientoCuentaCliente } from '../../../ducks/clientesDuck'
import { useCalcularMontoMensual } from '../../../hooks/useCalcularMontoMensual'
import { formatDinero } from '../../../utils/formatDinero'
import { FormatoFechaIso } from '../../../utils/FormatFecha'
import { catTipoPago } from '../../../constant/catTipoPago'

const useStyles = makeStyles((theme) => ({
    container: {
        width: '100%',
        padding: theme.spacing(2),
    },
    card: {
        padding: theme.spacing(2),
        borderRadius: '10px',
        marginBottom: theme.spacing(2),
        boxShadow: theme.shadows[2],
        transition: '0.3s',
    },
    row: {
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '0.9rem',
        marginBottom: theme.spacing(0.5),
    },
    label: {
        fontWeight: 500,
        color: '#555',
    },
    value: {
        fontWeight: 600,
    },
    statusIcon: {
        fontSize: '1.4rem',
    },
    badge: {
        fontSize: '0.75rem',
        padding: '3px 6px',
        borderRadius: '6px',
        backgroundColor: theme.palette.grey[300],
        fontWeight: 600,
        display: 'inline-block',
    },
    verde: { backgroundColor: green[100], borderLeft: `5px solid ${green[600]}` },
    rojo: { backgroundColor: red[100], borderLeft: `5px solid ${red[600]}` },
    azul: { backgroundColor: blue[100], borderLeft: `5px solid ${blue[600]}` },
    blanco: { backgroundColor: '#fff' },
}))

export default function TPlanPagos() {
    const classes = useStyles()
    const dispatch = useDispatch()

    const { referencia, cliente } = useSelector(store => store.cliente)
    const { mensualidades } = useCalcularMontoMensual({ IdPaquete: cliente.paquete?.Id_Paquete })

    useEffect(() => {
        dispatch(obtenerMovimientoCuentaCliente({ referencia }))
    }, [dispatch, referencia])

    if (!referencia)
        return <Alert severity="info">Seleccione un cliente</Alert>

    if (mensualidades.length === 0)
        return <Alert severity="info">No hay movimientos para mostrar</Alert>

    // Calcula el primer pago pendiente y no vencido
    const hoy = new Date()
    const proximoPagoIndex = mensualidades.findIndex(m =>
        !m.Pagado && new Date(m.FechaLimitePago) >= hoy
    )

    const getTipoColor = (row, index) => {
        if (row.Pagado) return classes.verde
        const fecha = new Date(row.FechaLimitePago)
        if (fecha < hoy) return classes.rojo
        if (index === proximoPagoIndex) return classes.azul
        return classes.blanco
    }

    const getTipoTexto = (id) => {
        if (id === catTipoPago.PLAN_PAGOS) return "CIE"
        if (id === catTipoPago.TARJETA_CREDITO) return "TDC"
        if (id === catTipoPago.EXTRA) return "EXTRA"
        return "OTRO"
    }

    return (
        <div className={classes.container}>
            <Box display="flex" flexWrap="wrap" justifyContent="space-between" mb={3} gap={2}>
                <Box p={2} bgcolor="#f5f5f5" borderRadius={8} flex="1 1 160px">
                    <Typography variant="subtitle2">Total de cuotas</Typography>
                    <Typography variant="h6">{mensualidades.length}</Typography>
                </Box>
                <Box p={2} bgcolor={green[100]} borderRadius={8} flex="1 1 160px">
                    <Typography variant="subtitle2">Cuotas cubiertas</Typography>
                    <Typography variant="h6">{mensualidades.filter(m => m.Pagado).length}</Typography>
                </Box>
                <Box p={2} bgcolor={red[100]} borderRadius={8} flex="1 1 160px">
                    <Typography variant="subtitle2">Cuotas vencidas</Typography>
                    <Typography variant="h6">
                        {
                            mensualidades.filter(m =>
                                !m.Pagado && new Date(m.FechaLimitePago) < new Date()
                            ).length
                        }
                    </Typography>
                </Box>
                <Box p={2} bgcolor={orange[100]} borderRadius={8} flex="1 1 160px">
                    <Typography variant="subtitle2">Por pagar (vigentes)</Typography>
                    <Typography variant="h6">
                        {
                            mensualidades.filter(m =>
                                !m.Pagado && new Date(m.FechaLimitePago) >= new Date()
                            ).length
                        }
                    </Typography>
                </Box>
            </Box>
            {mensualidades.map((row, index) => (
                <Paper key={row.IdPlanPago} className={`${classes.card} ${getTipoColor(row, index)}`}>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <div className={classes.row}>
                                <span className={classes.label}># Cuota</span>
                                <span className={classes.value}>{index + 1}</span>
                            </div>
                            <div className={classes.row}>
                                <span className={classes.label}>Monto</span>
                                <span className={classes.value}>{formatDinero(row.Monto)} {row?.Moneda?.Clave}</span>
                            </div>
                            <div className={classes.row}>
                                <span className={classes.label}>Monto por cubrir</span>
                                <span className={classes.value}>{formatDinero(row.MontoFaltante)} {row?.Moneda?.Clave}</span>
                            </div>
                        </Grid>

                        <Grid item xs={6}>
                            <div className={classes.row}>
                                <span className={classes.label}>Fecha límite</span>
                                <span className={classes.value}>{FormatoFechaIso({ date: row.FechaLimitePago, formato: "dd MMM yyyy" })}</span>
                            </div>
                            <div className={classes.row}>
                                <span className={classes.label}>Tipo de pago</span>
                                <span className={classes.badge}>{getTipoTexto(row?.CatTipoPago.IdCatTipoPago)}</span>
                            </div>
                            <div className={classes.row}>
                                <span className={classes.label}>Estatus</span>
                                {row.Pagado ? (
                                    <Tooltip title="Cubierto" arrow>
                                        <CheckCircleIcon className={classes.statusIcon} style={{ color: green[600] }} />
                                    </Tooltip>
                                ) : (
                                    <Tooltip title="Aún no cubierto" arrow>
                                        <ErrorIcon className={classes.statusIcon} style={{ color: orange[500] }} />
                                    </Tooltip>
                                )}
                            </div>
                        </Grid>
                    </Grid>
                </Paper>
            ))}
        </div>
    )
}
